import { UsersIcon } from "@heroicons/react/24/outline";
import React, { useContext, useEffect, useState } from "react";
import { Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import Card from "../../Components/Card";
import UserStat from "./UserStat";
import { TbMathAvg } from "react-icons/tb";
import Select from "../../Components/FormElements/Select";
import SocketContext from "../../Contexts/SocketContext";
import useOsSystemIcons from "../../Hooks/useOsSystemIcons";
import { decimalFormatter } from "../../Utils/Formatter";
import Spinner from "../../Components/Loaders/Spinner";
import useChart from "../../Hooks/useChart";
import OsSystemStackedBarChart from "./Charts/OsSystemStackedBarChart";
import AreaChart from "./Charts/AreaChart";
import DateRangePicker from "../../Components/Calendars/DateRangePicker";
import moment from "moment";
import useOsSystemColors from "../../Hooks/useOsSystemColors";
import { MdOutlineToday } from "react-icons/md";

const UserAnalytics = () => {
  const { getOsSystemIcon } = useOsSystemIcons();
  const { formatDataByPeriod } = useChart();

  const socket = useContext(SocketContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState();
  const dateFormatOptions = [
    { label: "Days", value: "days" },
    { label: "Weeks", value: "weeks" },
    { label: "Months", value: "months" },
    { label: "Years", value: "years" },
  ];
  const [selectedDateFormat, setSelectedDateFormat] = useState("days");
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: moment().subtract(30, "days").format("YYYY-MM-DD"),
    end: moment().format("YYYY-MM-DD"),
  });
  useEffect(() => {
    if (socket) {
      setLoading(true);
      socket.emit("showUserAnalytics", selectedDateRange);
      socket.on("userAnalytics", (data) => {
        setData(data);
        setLoading(false);
      });
    }
  }, [socket, selectedDateRange]);
  const { osColorMapping } = useOsSystemColors();
  const handleDateRangeSelect = (dateRange) => {
    setSelectedDateRange(dateRange);
    let diff = moment(dateRange.end).diff(dateRange.start, "day");
    if (diff <= 21) {
      setSelectedDateFormat("days");
      return;
    }
    if (diff < 60) {
      setSelectedDateFormat("weeks");
      return;
    }
    if (diff < 365) {
      setSelectedDateFormat("months");
      return;
    }
    setSelectedDateFormat("years");
  };
  return (
    <>
      <h1 className="text-3xl font-bold mb-4">User Analytics</h1>
      <div className="flex justify-between items-end">
        <div className="flex space-x-4 mt-4 max-w-lg">
          <UserStat
            loading={loading}
            icon={
              <UsersIcon className="h-6 w-6 text-white" aria-hidden="true" />
            }
            partial={parseInt(data?.total_users) ?? 0}
            label="Unique Users"
          />
          <UserStat
            loading={loading}
            icon={
              <TbMathAvg className="h-6 w-6 text-white" aria-hidden="true" />
            }
            partial={parseInt(data?.average_users) ?? 0}
            label="Avg. Users Per Day"
          />

          <UserStat
            loading={loading}
            icon={<MdOutlineToday className="h-6 w-6 text-white" />}
            partial={parseInt(data?.todays_users ?? 0)}
            total={parseInt(data?.average_users ?? 0)}
            label="Users Today"
          />
        </div>
        <div className="flex space-x-4">
          <DateRangePicker onDateSelect={handleDateRangeSelect} />
          <Select
            options={dateFormatOptions}
            onChange={setSelectedDateFormat}
            defaultIndex={dateFormatOptions.findIndex(
              (option) => option.value === selectedDateFormat
            )}
          />
        </div>
      </div>
      <div className="mt-8 space-y-8 pb-12">
        <Card>
          <p className="font-bold mb-2 ">Daily active users by os-system</p>

          {loading ? (
            <div className="h-[300px] flex items-center justify-center w-full">
              <Spinner height="h-8 w-8" />
            </div>
          ) : (
            <OsSystemStackedBarChart
              data={formatDataByPeriod(
                data?.player_count_per_os_system,
                selectedDateFormat
              )}
              showTooltipPercent
              showTooltipTotal
            />
          )}
        </Card>
        <Card>
          <h2 className="font-bold mb-2">Average flight duration</h2>

          {loading ? (
            <div className="h-[300px] flex items-center justify-center w-full">
              <Spinner height="h-8 w-8" />
            </div>
          ) : (
            <AreaChart
              data={formatDataByPeriod(
                data?.average_flight_duration,
                selectedDateFormat,
                "average"
              )}
              toolTipValueName={"avg duration"}
              showDecimalLabel
            />
          )}
          <h2 className="font-bold mb-2 mt-12">
            Average flight duration by os system
          </h2>
          <p className="mb-4 text-sm text-gray-600 max-w-xl"></p>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center w-full">
              <Spinner height="h-8 w-8" />
            </div>
          ) : (
            <OsSystemStackedBarChart
              data={formatDataByPeriod(
                data?.average_flight_duration_per_os_system,
                selectedDateFormat
              )}
              tooltipUnit="min"
            />
          )}
        </Card>
        <div className="">
          <Card>
            <h2 className="font-bold mb-2">OS Distribution</h2>
            <p className="text-gray-500">
              Distribution of users by operating system from{" "}
              {moment(selectedDateRange.start).format("dddd MMMM Do")} to{" "}
              {moment(selectedDateRange.end).format("dddd MMMM Do")}
            </p>
            <div className="grid grid-cols-2 gap-4 ">
              {!loading && (
                <ol className="place-self-center w-full">
                  <li
                    key={"header"}
                    className="grid grid-cols-12 text-sm font-semibold"
                  >
                    <div className="col-span-5 py-2">
                      <p className="text-sm  text-gray-600">{"OS System"}</p>
                    </div>
                    <div className="col-span-3  py-2 text-right">
                      <p className="">{"Players"}</p>
                    </div>
                    <div className="col-span-4 py-2 text-right">
                      <p className="">{"Players in %"}</p>
                    </div>
                  </li>

                  {data?.os_distribution
                    .sort((a, b) => parseInt(a.count) - parseInt(b.count))
                    .reverse()
                    .map((item) => {
                      return (
                        <li key={item.os_system} className="grid grid-cols-12 ">
                          <div className="col-span-5 flex items-center space-x-2 border-t border-gray-200  py-2">
                            <span className="flex-shrink-0">
                              {getOsSystemIcon(item.os_system, "h-6 w-6")}
                            </span>
                            <p className="text-sm  text-gray-600">
                              {item.os_system}
                            </p>
                          </div>
                          <div className="col-span-3 border-t border-gray-200 py-2 text-right">
                            <p className="">
                              {decimalFormatter.format(item.count)}
                            </p>
                          </div>
                          <div className="col-span-4 border-t border-gray-200 py-2 text-right">
                            <p className="">
                              {(
                                (item.count /
                                  data?.os_distribution.reduce(
                                    (acc, curr) => acc + parseInt(curr.count),
                                    0
                                  )) *
                                100
                              ).toFixed(0) + "%"}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  <li key={"total"} className="grid grid-cols-12 font-bold ">
                    <div className="col-span-5 flex items-center space-x-2 border-t border-gray-400 py-2">
                      <span className="flex-shrink-0 h-6 w-6"></span>
                      <p className="text-sm">{"total"}</p>
                    </div>
                    <div className="col-span-3 border-t border-gray-400 py-2 text-right">
                      <p className="">
                        {decimalFormatter.format(
                          data?.os_distribution.reduce(
                            (acc, curr) => acc + parseInt(curr.count),
                            0
                          )
                        )}
                      </p>
                    </div>
                    <div className="col-span-4 border-t border-gray-400 py-2 "></div>
                  </li>
                </ol>
              )}
              {loading ? (
                <div className="h-[300px] flex items-center justify-center w-full">
                  <Spinner height="h-8 w-8" />
                </div>
              ) : (
                <ResponsiveContainer height={300}>
                  <PieChart>
                    <Pie
                      data={data?.os_distribution?.map((item) => ({
                        count: parseInt(item.count),
                        os_system: item.os_system,
                      }))}
                      dataKey="count"
                      nameKey="os_system"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ payload }) => payload.os_system}
                    >
                      {data?.os_distribution
                        ?.map((item) => ({
                          count: parseInt(item.count),
                          os_system: item.os_system,
                        }))
                        ?.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={osColorMapping[entry.os_system] || "#000000"}
                          />
                        ))}
                    </Pie>
                    <Tooltip
                      content={
                        <CustomTooltip
                          sum={data?.os_distribution.reduce(
                            (acc, curr) => acc + parseInt(curr.count),
                            0
                          )}
                        />
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

const CustomTooltip = ({ active, payload, label, sum }) => {
  console.log(payload, sum);
  if (active && payload && payload.length) {
    return (
      <Card>
        <p className="text-xs font-bold mb-2">{payload[0]?.name}</p>
        <div className="flex items-center space-x-4">
          <p>{decimalFormatter.format(payload[0].value)}</p>
          {sum > 0 && (
            <p>{((payload[0].value / sum) * 100).toFixed(0) + "%"}</p>
          )}
        </div>
      </Card>
    );
  }
};

export default UserAnalytics;
